import { User } from "oidc-client-ts";
/**
 * Rejection carried out of the transport so callers can branch on intent
 * without sniffing message strings.
 */
export class HostAuthError extends Error {
    reason;
    constructor(reason) {
        super(`host-auth ${reason}`);
        this.name = "HostAuthError";
        this.reason = reason;
    }
}
/**
 * Build an in-memory oidc `User` from a host token. This is NOT persisted and
 * never written to `oidc.user:*` storage, and no `UserManager` is involved; it
 * is a distinct, host-owned session that merely conforms to the `User` shape
 * existing consumers (`state.user.profile.*`, `state.user.access_token`) read.
 */
export function hostUserFromToken(token) {
    return new User({
        access_token: token.accessToken,
        token_type: "Bearer",
        profile: {
            sub: token.profile?.sub ?? "",
            email: token.profile?.email,
            aadTenantId: token.profile?.aadTenantId,
            aadObjectId: token.profile?.aadObjectId,
        },
        ...(token.expiresAt !== null ? { expires_at: token.expiresAt } : {}),
    });
}
export function createHostAuthSession(opts) {
    let current = null;
    const remember = (token) => {
        current = token;
        return token;
    };
    // Coalesce concurrent handshakes. The proactive refresh timer, getFreshBearerToken,
    // and recoverAuthAfter401 can all ask for a token at once; each transport call opens
    // its own postMessage listener, so a single desktop reply would satisfy one waiter and
    // leave the others to time out and tear down a healthy session. Sharing one in-flight
    // request means one handshake, one reply, one shared token.
    let inFlight = null;
    const request = (reason) => {
        if (inFlight)
            return inFlight;
        const pending = (reason === undefined ? opts.requestToken() : opts.requestToken(reason))
            .then(remember)
            .finally(() => {
            if (inFlight === pending)
                inFlight = null;
        });
        inFlight = pending;
        return pending;
    };
    // Listeners receive both unsolicited transport pushes and in-band terminal
    // failures (reportError), so the provider has a single teardown signal.
    const listeners = new Set();
    const emit = (reason) => {
        for (const listener of listeners)
            listener(reason);
    };
    let transportUnsubscribe;
    let transportInstalled = false;
    return {
        requestInitialToken() {
            return request();
        },
        refresh(reason) {
            return request(reason);
        },
        getToken() {
            return current;
        },
        toUser(token) {
            return hostUserFromToken(token);
        },
        subscribe(onError) {
            listeners.add(onError);
            if (!transportInstalled) {
                transportInstalled = true;
                transportUnsubscribe = opts.subscribe?.(emit);
            }
            return () => {
                listeners.delete(onError);
                if (listeners.size === 0) {
                    transportUnsubscribe?.();
                    transportUnsubscribe = undefined;
                    transportInstalled = false;
                }
            };
        },
        reportError(reason) {
            emit(reason);
        },
        requestLogout() {
            opts.requestLogout?.();
        },
    };
}
let configured = null;
/**
 * Register (or clear) the host-auth session. Call once at app boot, before
 * AuthProvider mounts, when embedded-mode detection passes. When a session is
 * configured the AuthProvider uses the host-auth path; otherwise the standalone
 * OIDC path runs unchanged.
 */
export function configureHostAuth(session) {
    configured = session;
}
export function getHostAuthSession() {
    return configured;
}
export function isHostAuthActive() {
    return configured !== null;
}
let teamsAuthWrapperActive = false;
/**
 * Register whether healthOS is embedded inside the Microsoft Teams auth
 * wrapper. Call once at app boot alongside {@link configureHostAuth}; the
 * host app (which resolves the parent origin) is the one source of truth for
 * this, so this package stays transport-free.
 */
export function configureIsInMsTeamsAuthWrapper(active) {
    teamsAuthWrapperActive = active;
}
export function isInMsTeamsAuthWrapper() {
    return teamsAuthWrapperActive;
}
let impersonationActive = false;
let impersonationProfile;
let impersonationExpiresAt = null;
/**
 * Register whether this session is a support "Log in as user" impersonation
 * session rather than a real sign-in, plus the profile to display and the
 * token's expiry (unix seconds) while it is active. Call once at boot alongside
 * {@link configureHostAuth}, before AuthProvider mounts, when a redeemed
 * impersonation token is installed instead of the desktop/Teams host-auth path.
 * `profile`/`expiresAt` are ignored (and any previously-stashed values cleared)
 * when `active` is false, so nothing stale can survive a deactivation.
 */
export function configureImpersonation(active, profile, expiresAt) {
    impersonationActive = active;
    impersonationProfile = active ? profile : undefined;
    impersonationExpiresAt = active ? (expiresAt ?? null) : null;
}
export function isImpersonationActive() {
    return impersonationActive;
}
/** The profile stashed by {@link configureImpersonation}, for display (e.g. a "Viewing as" banner). */
export function getImpersonationProfile() {
    return impersonationProfile;
}
/** The impersonation token's expiry (unix seconds), for the banner countdown and clean end. */
export function getImpersonationExpiresAt() {
    return impersonationExpiresAt;
}
//# sourceMappingURL=hostAuth.js.map